import React, { useRef } from 'react';
import { Download20, Close20 } from '@carbon/icons-react';
import { Modal, Button } from 'carbon-components-react';
import {
  HTMLViewer,
  IFrameViewer,
  ResponsePDF,
  ResponsePDFDownloadButton,
  ResponseBodyDownload,
  ResponseImage,
  ResponseImageDownloadButton
} from 'components';
import type { ResponsePDFRef } from 'components/ResponsePDF';
import type { ResponseImageRef } from 'components/ResponseImage';

export const ResponseBodyModal = ({
  previewData,
  referral,
  enquiryId,
  inboundEmailId,
  show,
  setShow
}: {
  previewData: any;
  referral: any;
  enquiryId: number;
  inboundEmailId: number;
  show: boolean;
  setShow: (show: boolean) => void;
}) => {
  return (
    <Modal
      open={show}
      passiveModal
      size="lg"
      className={`super-modal mobile-view mobile-get-plans-super-modal`}
    >
      <div className="mobile-view-modal-header">
        <div className="mobile-view-modal-header-content">
          <div className="mobile-view-modal-header-text">
            <div className="mobile-view-modal-title">{referral.name}</div>
            <div className="mobile-view-modal-subtitle">Response body</div>
          </div>
          <div className="mobile-view-modal-actions">
            <ResponseBodyDownload
              html={previewData?.body}
              referralId={referral?.externalId}
              inboundEmailId={inboundEmailId}
              enquiryId={enquiryId}
            >
              {({ downloadResponseBody, isDownloading }) => (
                <Button
                  kind="ghost"
                  size="field"
                  onClick={() => downloadResponseBody()}
                  disabled={isDownloading}
                  className="mobile-view-modal-action-button"
                >
                  <Download20 fill="#0F62FE" />
                </Button>
              )}
            </ResponseBodyDownload>
            <Button
              kind="ghost"
              size="field"
              onClick={() => {
                setShow(false);
              }}
              className="mobile-view-modal-action-button"
            >
              <Close20 fill="#0F62FE" />
            </Button>
          </div>
        </div>
      </div>
      <div className="mobile-view-modal-content">
        <HTMLViewer
          html={previewData?.body}
          title="Response body"
          inboundEmailId={inboundEmailId}
          referralId={referral?.externalId}
          enquiryId={enquiryId}
        />
      </div>
    </Modal>
  );
};

export const ResponseWebMapModal = ({
  previewData,
  referral,
  show,
  setShow
}: {
  previewData: any;
  referral: any;
  show: boolean;
  setShow: (show: boolean) => void;
}) => {
  return (
    <Modal
      open={show}
      passiveModal
      size="lg"
      className={`super-modal mobile-view mobile-get-plans-super-modal`}
    >
      <div className="mobile-view-modal-header">
        <div className="mobile-view-modal-header-content">
          <div className="mobile-view-modal-header-text">
            <div className="mobile-view-modal-title">{referral.name}</div>
            <div className="mobile-view-modal-subtitle">Web map</div>
          </div>
          <div className="mobile-view-modal-actions">
            <Button
              kind="ghost"
              size="field"
              onClick={() => {
                setShow(false);
              }}
              className="mobile-view-modal-action-button"
            >
              <Close20 fill="#0F62FE" />
            </Button>
          </div>
        </div>
      </div>
      <div className="mobile-view-modal-content" style={{ padding: 0 }}>
        <IFrameViewer
          className="get-plans-responses-content-viewer-previewer-webmap"
          source={previewData?.body}
          selectedData={referral}
          title={'Web map'}
          viewerContentHeight={200}
        />
      </div>
    </Modal>
  );
};

export const ResponsePdfModal = ({
  previewData,
  enquiryId,
  referral,
  show,
  setShow
}: {
  previewData: any;
  enquiryId: number;
  referral: any;
  show: boolean;
  setShow: (show: boolean) => void;
}) => {
  // Ref for the new mobile PDF viewer to access download functionality
  const pdfViewerRef = useRef<ResponsePDFRef>(null);
  return (
    <Modal
      open={show}
      passiveModal
      size="lg"
      className={`super-modal mobile-view mobile-get-plans-super-modal`}
    >
      <div className="mobile-view-modal-header">
        <div className="mobile-view-modal-header-content">
          <div className="mobile-view-modal-header-text">
            <div className="mobile-view-modal-title">{referral.name}</div>
            <div className="mobile-view-modal-subtitle">
              {previewData?.name}
            </div>
          </div>
          <div className="mobile-view-modal-actions">
            {/* Example usage of the new ResponsePDFDownloadButton component */}
            <ResponsePDFDownloadButton
              responsePDFRef={pdfViewerRef}
              buttonProps={{
                className: 'mobile-view-modal-action-button'
              }}
            />
            <Button
              kind="ghost"
              size="field"
              onClick={() => {
                setShow(false);
              }}
              className="mobile-view-modal-action-button"
            >
              <Close20 fill="#0F62FE" />
            </Button>
          </div>
        </div>
      </div>
      <div className="mobile-view-modal-content" style={{ padding: 0 }}>
        <ResponsePDF
          ref={pdfViewerRef}
          fileId={previewData?.id}
          enquiryId={enquiryId}
          fileUrl={previewData?.body}
          fileName={previewData?.name}
          // viewerContentHeight="70vh"
        />
      </div>
    </Modal>
  );
};

export const ResponseImageModal = ({
  previewData,
  enquiryId,
  referral,
  show,
  setShow
}: {
  previewData: any;
  enquiryId: number;
  referral: any;
  show: boolean;
  setShow: (show: boolean) => void;
}) => {
  // Ref for the new mobile image viewer to access download functionality
  const imageViewerRef = useRef<ResponseImageRef>(null);
  return (
    <Modal
      open={show}
      passiveModal
      size="lg"
      className={`super-modal mobile-view mobile-get-plans-super-modal`}
    >
      <div className="mobile-view-modal-header">
        <div className="mobile-view-modal-header-content">
          <div className="mobile-view-modal-header-text">
            <div className="mobile-view-modal-title">{referral.name}</div>
            <div className="mobile-view-modal-subtitle">
              {previewData?.name}
            </div>
          </div>
          <div className="mobile-view-modal-actions">
            {/* Example usage of the new ResponseImageDownloadButton component */}
            <ResponseImageDownloadButton
              responseImageRef={imageViewerRef}
              buttonProps={{
                className: 'mobile-view-modal-action-button'
              }}
            />
            <Button
              kind="ghost"
              size="field"
              onClick={() => {
                setShow(false);
              }}
              className="mobile-view-modal-action-button"
            >
              <Close20 fill="#0F62FE" />
            </Button>
          </div>
        </div>
      </div>
      <div className="mobile-view-modal-content" style={{ padding: 0 }}>
        <ResponseImage
          ref={imageViewerRef}
          fileId={previewData?.id}
          enquiryId={enquiryId}
          imageUrl={previewData?.body}
          imageName={previewData?.name}
        />
      </div>
    </Modal>
  );
};
